import React from 'react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

// Custom tooltip plugin
const customTooltipPlugin = {
    id: 'customTooltip',
    afterDraw: (chart) => {
        const tooltip = chart.tooltip;
        
        if (tooltip && tooltip.opacity > 0) {
            const ctx = chart.ctx;
            const activePoints = chart.getActiveElements();
            
            if (activePoints.length > 0) {
                const point = activePoints[0];
                const dataIndex = point.datasetIndex;
                const index = point.index;
                const meta = chart.getDatasetMeta(dataIndex);
                const bar = meta.data[index];
                
                const barLabel = chart.data.labels[index];
                const barValue = chart.data.datasets[dataIndex].data[index];

                const tooltipY = bar.y - 80;
                const tooltipWidth = 160;
                const tooltipHeight = 40;
                
                ctx.save();
                
                // Tooltip background
                ctx.fillStyle = '#2d3748';
                
                // Tooltip arrow
                ctx.beginPath();
                ctx.closePath();
                ctx.fill();
                
                // Tooltip text
                ctx.fillStyle = 'white';
                ctx.font = 'bold 13px Arial';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                
                ctx.font = '13px Arial';
                ctx.restore();
            }
        }
    }
};

// Register the plugin
ChartJS.register(customTooltipPlugin);

const options = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: { mode: 'index', intersect: false },
    plugins: {
        legend: { position: 'top', display: false },
        title: { display: false },
        tooltip: { 
            enabled: true,
            mode: 'nearest',
            intersect: true,
            external: function() {
                return false; // This will trigger our custom tooltip
            }
        },
        customTooltip: true
    },
    scales: {
        y: {
            min: 0,
            max: 100,
            ticks: {
                stepSize: 10,
                color: '#00000',
                font: { size: 12 },
                padding: 5
            },
            grid: {
                color: '#d0d0d0',
                lineWidth: 1,
                drawTicks: false
            },
            border: { 
                display: true,
                color: '#000000',
                width: 2
            },
        },
        x: {
            ticks: {
                color: '#666',
                font: { size: 14 },
                padding: 5
            },
            grid: { display: false },
            border: { 
                display: true,
                color: '#000000',
                width: 2
            },
        }
    },
    layout: {
        padding: {
            top: 10,
            right: 15,
            bottom: 10,
            left: 15
        }
    }
};

const defaultLabels = ['Content', 'Speech'];
const defaultDataValues = [65, 36];
const defaultBarColors = ['#6E5DC6', '#52B8CC'];
const defaultLegendLabels = [
    { id: 'content', label: 'Content', color: '#7c4dff' },
    { id: 'speech', label: 'Speech', color: '#4fc3f7' }
];
const defaultTitle = 'Batch performance overview';

export default function LinearBarGraph({
    dataValues = defaultDataValues,
    labels = defaultLabels,
    barColors = defaultBarColors,
    title = defaultTitle,
    legendLabels = defaultLegendLabels,
    yAxisMax = 100
} = {}) {
    const data = {
        labels: labels,
        datasets: [
            {
                data: dataValues,
                backgroundColor: barColors,
                borderWidth: 0,
                barPercentage: 0.6,
                categoryPercentage: 0.8,
                borderRadius: {
                    topLeft: 4,
                    topRight: 4,
                    bottomLeft: 0,
                    bottomRight: 0
                }
            }
        ]
    };

    // Update options with dynamic yAxisMax
    const chartOptions = {
        ...options,
        scales: {
            ...options.scales,
            y: {
                ...options.scales.y,
                max: yAxisMax,
                ticks: {
                    ...options.scales.y.ticks,
                    stepSize: yAxisMax === 5 ? 1 : 10
                }
            }
        }
    };

    return (
        <Box display='flex' flexDirection='column' gap='12px' width='100%'>
            <Typography variant='h5-medium' color='neutral.clr-700'>
                {title}
            </Typography>
            <div style={{
                width: "100%",
                background: '#FCFAFA',
                // border: "1px solid lightgrey",
                borderRadius: 12,
                padding: 20,
                textAlign: 'center',
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: 'relative',
                minHeight: 531
            }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center" , position: 'relative' }}>
                        <div style={{
                            transform: "rotate(-90deg)",
                            fontSize: 14,
                            color: '#666',
                            fontWeight: 'bold',
                            marginRight: 5,
                            minWidth: 40,
                            position: 'absolute',
                            left: -10,
                            bottom: 60
                        }}>
                            Score
                        </div>
                        <div style={{ flexGrow: 1 }}>
                            <Bar options={chartOptions} data={data} />
                        </div>
                    </div>
                    {/* X-axis label */}
                    <div style={{
                        fontSize: 14,
                        color: '#666',
                        fontWeight: 'bold',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        marginLeft: 50
                    }}>
                        Parameters
                    </div>
                    {/* Custom legend */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: 20,
                        marginTop: 20,
                        alignItems: 'center',
                        marginLeft: 50
                    }}>
                        {legendLabels.map((legend, idx) => (
                            <div key={legend.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: legend.color,
                                    borderRadius: '50%'
                                }} />
                                <span style={{ fontSize: 14, color: '#666' }}>{legend.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Box>
    );
}